import {
    List,
    Datagrid,
    TextField,
    ImageField,
    EditButton,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    ImageInput,
    required,
    DateField,
    NumberField,
    NumberInput,
    useNotify,
    useRefresh,
    useRecordContext,
    Button,
    FileInput,
    FileField,
    BooleanField,
    BooleanInput,
} from 'react-admin';
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { getBrandImageEndpoint } from '../config';

// Custom component for brand image upload
const BrandImageUpload = ({ imageType, label }) => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (file) => {
        if (!file || !record?.id) return;
        
        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                getBrandImageEndpoint(record.id, imageType),
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );
            
            if (!response.ok) {
                throw new Error('Upload failed');
            }
            
            const data = await response.json();
            notify(`${label} uploaded successfully`, { type: 'success' });
            refresh();
        } catch (error) {
            notify(`Failed to upload ${label.toLowerCase()}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const currentImageUrl = record?.[`${imageType}_image_url`];

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                {label}
            </Typography>
            {currentImageUrl && (
                <Box sx={{ mb: 1 }}>
                    <img 
                        src={currentImageUrl} 
                        alt={`${label}`}
                        style={{ 
                            maxWidth: imageType === 'logo' ? '150px' : '300px', 
                            maxHeight: imageType === 'logo' ? '80px' : '120px',
                            objectFit: 'contain',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }} 
                    />
                </Box>
            )}
            <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file);
                }}
                disabled={uploading}
                style={{ marginBottom: '8px' }}
            />
            {uploading && <Typography variant="caption">Uploading...</Typography>}
        </Box>
    );
};

export const BrandList = () => (
    <List>
        <Datagrid>
            <TextField source="id" />
            <TextField source="name" />
            <ImageField source="logo_image_url" title="Logo" />
            <ImageField source="banner_image_url" title="Banner" />
            <TextField source="description" />
            <NumberField source="ordering" />
            <BooleanField source="is_active" />
            <DateField source="CreatedAt" showTime={true}/>
            <DateField source="UpdatedAt" showTime={true}/>
            <EditButton />
        </Datagrid>
    </List>
);

export const BrandEdit = () => (
    <Edit>
        <SimpleForm>
            <TextInput source="name" validate={[required()]} />
            <TextInput source="description" multiline rows={4} validate={[required()]} />
            <BrandImageUpload imageType="logo" label="Logo Image" />
            <BrandImageUpload imageType="banner" label="Banner Image" />
            <NumberInput source="ordering" defaultValue={0}/>
            <BooleanInput source="is_active" defaultValue={true} />
        </SimpleForm>
    </Edit>
);

export const BrandCreate = () => (
    <Create>
        <SimpleForm>
            <TextInput source="name" validate={[required()]} />
            <TextInput source="description" multiline rows={4} validate={[required()]} />
            <NumberInput source="ordering" defaultValue={0}/>
            <BooleanInput source="is_active" defaultValue={true} />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Note: Logo and banner images can be uploaded after creating the brand.
            </Typography>
        </SimpleForm>
    </Create>
); 